import { useId, useState, type FormEvent } from "react";

import { api, ApiError } from "../lib/api";

/** «Editar grupo» (Eduardo 2026-10-14, torneo 71: grupos «4 / 3»): nombre y
 * cupo del grupo sin salir de la pantalla. Cupo vacío = sin límite; no puede
 * quedar por debajo de los integrantes. Renombrar no toca partidos. */

export interface GroupEditFormProps {
  token: string;
  tournamentId: number;
  groupId: number;
  name: string;
  capacity: number | null | undefined;
  memberCount: number;
  /** Sin permiso: no se muestra el botón. */
  canEdit?: boolean;
  onSaved: (message: string) => void | Promise<void>;
}

/** El grupo tiene más integrantes que su cupo (p. ej. «4 / 3»). */
export function groupOverCapacity(capacity: number | null | undefined, count: number): boolean {
  return capacity != null && capacity > 0 && count > capacity;
}

export function validateGroupEdit(name: string, capacityRaw: string, memberCount: number): string | null {
  if (!name.trim()) return "Escribe el nombre del grupo.";
  const raw = capacityRaw.trim();
  if (raw === "") return null;
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 1) {
    return "El cupo debe ser un número entero de 1 en adelante, o vacío para sin límite.";
  }
  if (value < memberCount) return `El grupo ya tiene ${memberCount} jugadores: el cupo no puede ser menor.`;
  return null;
}

export default function GroupEditForm({
  token,
  tournamentId,
  groupId,
  name,
  capacity,
  memberCount,
  canEdit = true,
  onSaved,
}: GroupEditFormProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const [draftCapacity, setDraftCapacity] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  if (!canEdit) return null;

  function start() {
    setDraftName(name);
    setDraftCapacity(capacity != null && capacity > 0 ? String(capacity) : "");
    setError(null);
    setOpen(true);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const problem = validateGroupEdit(draftName, draftCapacity, memberCount);
    if (problem) {
      setError(problem);
      return;
    }
    const raw = draftCapacity.trim();
    const finalName = draftName.trim();
    setSaving(true);
    setError(null);
    try {
      await api.groups.update(token, tournamentId, groupId, {
        name: finalName,
        capacity: raw === "" ? null : Number(raw),
      });
      setOpen(false);
      await onSaved(`Grupo «${finalName}» guardado.`);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message || "No se pudo guardar el grupo." : "Error de red. Intenta otra vez.",
      );
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="btn btn--ghost grp-edit__open" onClick={start} aria-label={`Editar grupo: ${name}`}>
        Editar grupo
      </button>
    );
  }

  return (
    <form className="grp-edit" onSubmit={submit} aria-label={`Editar ${name}`} noValidate>
      <label htmlFor={`${id}-name`}>Nombre</label>
      <input
        id={`${id}-name`}
        type="text"
        required
        maxLength={120}
        value={draftName}
        onChange={(e) => setDraftName(e.target.value)}
        disabled={saving}
        autoFocus
      />
      <label htmlFor={`${id}-cap`}>Cupo</label>
      <input
        id={`${id}-cap`}
        type="number"
        min={1}
        step={1}
        inputMode="numeric"
        value={draftCapacity}
        onChange={(e) => setDraftCapacity(e.target.value)}
        placeholder="Sin límite"
        aria-describedby={`${id}-hint`}
        disabled={saving}
      />
      <p id={`${id}-hint`} className="grp-edit__hint">
        Vacío = sin límite. Hoy tiene {memberCount} {memberCount === 1 ? "jugador" : "jugadores"}.
      </p>
      {error ? (
        <p className="grp-edit__error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="grp-edit__actions">
        <button type="submit" className="btn" disabled={saving}>
          {saving ? "Guardando…" : "Guardar"}
        </button>
        <button type="button" className="btn btn--ghost" onClick={() => setOpen(false)} disabled={saving}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
